/**
 * Executions Subscription Policy (TV1-1971)
 *
 * Owns the Actividad tab's FLAT data: one row per Rule Execution (not per
 * Batch). Replaces `batchesSubscriptionPolicy` as the list driver.
 *
 * Real-time strategy (memory `pv3 realtime signal mode`): a SINGLE signal-mode
 * subscription on the `RuleExecutions` readmodel — NO polling, NOT one
 * subscription per row. pv3 signal-mode subscriptions carry no data: the server
 * sends `{ type: 'signal' }` pings and the client refetches. The refetch is
 * debounced (~200ms) to coalesce the bursts that a high-throughput account
 * produces (executions are N× the old batch rows).
 *
 * The snapshot + each refetch go through the `-search` route
 * (`RuleExecutionsSearch`) — the base `/rule-executions/:id?` route rejects a
 * `filter`/`limit`/`offset` query param (no `defaultQuerySchemas.query` block).
 * The search handler already sorts `startedAt desc`, applies the generic
 * `params.filter`, and is account-scoped by `FilterByAccountIdTransformer`.
 *
 * Mirrors the retired `batchesSubscriptionPolicy` init/debounce/cleanup shape
 * and the `priceListDataPolicy` snapshot+signal→refetch pattern.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType, ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { RulesExecutionsReadmodelType } from '@bett3r-dev/teselly-domain';
import { ActivityState, ExecutionFilter } from '../state';
import { ActivityCommand } from '../commands';

/** The composable clauses read from `ui` when building the search filter. */
type ExecutionFilterInputs = Pick<
  ActivityState['ui'],
  | 'executionFilter'
  | 'triggeringEventNameFilter'
  | 'batchIdFilter'
  | 'hierarchyLevelFilter'
  | 'targetEntityIdFilter'
>;

const SIGNAL_DEBOUNCE_MS = 200;

/** The `RuleExecutionsSearch` `params.filter` shape (a CollectionFilter[]). */
type SearchFilter = NonNullable<
  Parameters<ClientLibraryType['queries']['RuleExecutionsSearch']>[0]
>['filter'];

/**
 * Map the status chip to the `RuleExecutionsSearch` `params.filter` (TV1-1971,
 * slice 2). The status filter runs SERVER-SIDE over the existing
 * `[ 'accountId', 'status' ]` index — no new index. `running` groups the
 * `pending` + `in-progress` statuses; `all` sends no filter.
 */
export const executionStatusFilter = (
  filter: ExecutionFilter
): SearchFilter | undefined => {
  if ( filter === 'running' ) {
    return [{ field: 'status', op: 'in', value: [ 'pending', 'in-progress' ]}];
  }
  if ( filter === 'failed' ) {
    return [{ field: 'status', op: 'eq', value: 'failed' }];
  }
  if ( filter === 'completed' ) {
    return [{ field: 'status', op: 'eq', value: 'completed' }];
  }
  return undefined;
};

/**
 * Compose the flat Actividad list's SERVER-SIDE search filter (TV1-1971, slices
 * 3–4) from the independent list dimensions, AND-ed into one `params.filter`
 * array for the (unchanged, generic-filter) `RuleExecutionsSearch` route:
 *
 *   - the STATUS chip     → `executionStatusFilter` (slice 2; `[ 'accountId',
 *     'status' ]` index),
 *   - the trigger-TYPE chip → `{ triggeringEventName eq }` (`[ 'accountId',
 *     'triggeringEventName', 'startedAt' ]` index),
 *   - the occurrence DRILL  → `{ batchId eq }` (existing `[ 'accountId',
 *     'batchId' ]` index),
 *   - the entity-LEVEL chip → `{ hierarchyLevel eq }` (slice 4; `[ 'accountId',
 *     'hierarchyLevel', 'startedAt' ]` index),
 *   - the entity DRILL      → `{ targetEntityId eq }` (existing `[ 'accountId',
 *     'targetEntityId', 'startedAt' ]` index).
 *
 * The clauses COMPOSE: an active status filter is never clobbered when a trigger
 * chip / drill / hierarchy chip / entity drill is added — each present dimension
 * contributes its own clause. Returns `undefined` when NO dimension is active (the
 * default all-executions view), so the route sends no `filter` at all.
 */
export const composeExecutionsFilter = (
  ui: ExecutionFilterInputs
): SearchFilter | undefined => {
  const clauses: NonNullable<SearchFilter> = [];

  const status = executionStatusFilter( ui.executionFilter );
  if ( status ) clauses.push( ...status );

  if ( ui.triggeringEventNameFilter ) {
    clauses.push({
      field: 'triggeringEventName',
      op: 'eq',
      value: ui.triggeringEventNameFilter
    });
  }

  if ( ui.batchIdFilter ) {
    clauses.push({ field: 'batchId', op: 'eq', value: ui.batchIdFilter });
  }

  if ( ui.hierarchyLevelFilter ) {
    clauses.push({ field: 'hierarchyLevel', op: 'eq', value: ui.hierarchyLevelFilter });
  }

  if ( ui.targetEntityIdFilter ) {
    clauses.push({ field: 'targetEntityId', op: 'eq', value: ui.targetEntityIdFilter });
  }

  return clauses.length ? clauses : undefined;
};

/**
 * History window fetched in ONE shot. The default view is all executions,
 * `startedAt desc`, account-scoped, existing page size — client-side paging over
 * this window (coherent for dev volumes; server-side paging is the follow-up if
 * history grows past this cap). Matches the retired batch policy's window.
 */
const EXECUTIONS_HISTORY_WINDOW = 200;

export const createExecutionsSubscriptionPolicy = (
  backend: ClientLibraryType
): Policy<ActivityState, ActivityCommand> => {
  // Shared with `onStateChange` so a status-chip change re-runs the (debounced)
  // account-scoped refetch with the new `params.filter`. Assigned in `init`,
  // cleared on unmount.
  let scheduleRefetch: (() => void ) | null = null;

  return {
  name: 'activity-executions-subscription',

  // Re-fetch when ANY server-side list filter changes: the status chip
  // (`ui/setExecutionFilter`), the trigger-type chip
  // (`ui/setTriggeringEventFilter`), the occurrence drill
  // (`ui/setBatchFilter`), the entity-level chip
  // (`ui/setHierarchyLevelFilter`), or the entity drill
  // (`ui/setEntityFilter`). Each is a SERVER-SIDE filter, so a change needs a
  // fresh snapshot.
  shouldReact: ( prev, next ) =>
    prev.ui.executionFilter !== next.ui.executionFilter ||
    prev.ui.triggeringEventNameFilter !== next.ui.triggeringEventNameFilter ||
    prev.ui.batchIdFilter !== next.ui.batchIdFilter ||
    prev.ui.hierarchyLevelFilter !== next.ui.hierarchyLevelFilter ||
    prev.ui.targetEntityIdFilter !== next.ui.targetEntityIdFilter,

  onStateChange: () => {
    scheduleRefetch?.();
  },

  init: ({ dispatch, getState }) => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const fetchExecutions = async () => {
      if ( closed ) return;
      try {
        // Read ALL active list filters at fire time so the snapshot / debounced
        // refetch always reflects the current, COMPOSED filter (status chip +
        // trigger-type chip + occurrence drill + entity-level chip + entity
        // drill), server-side via the readmodel indexes. Each present dimension
        // contributes its own AND-ed clause.
        const filter = composeExecutionsFilter( getState().ui );
        const result = await backend.queries.RuleExecutionsSearch({
          limit: EXECUTIONS_HISTORY_WINDOW,
          offset: 0,
          ...( filter ? { filter } : {})
        });
        if ( closed ) return;

        dispatch(
          {
            type: 'automation/activity/data/setExecutions',
            payload: {
              executions: ( result?.items ?? []) as RulesExecutionsReadmodelType[]
            }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        if ( closed ) return;
        console.error( '[activity] Executions search error:', error );
        dispatch(
          {
            type: 'automation/activity/loading/setExecutionsLoading',
            payload: { isLoading: false }
          },
          { source: 'api' }
        );
      }
    };

    // The FILTER-INDEPENDENT per-status counts (TV1-1971, slice 2). SEPARATE from
    // and independent of the (server-filtered) list fetch above: it always reads
    // the full per-status picture for the account via the dedicated
    // `RuleExecutionsStatusCounts` query (one raw-SQL `GROUP BY`), so the chip
    // badges + the LiveStrip/page-header running pill stay correct even when the
    // list chip is on `completed`/`failed`. No `filter` is ever passed.
    const fetchStatusCounts = async () => {
      if ( closed ) return;
      try {
        const counts = await backend.queries.RuleExecutionsStatusCounts();
        if ( closed ) return;

        dispatch(
          {
            type: 'automation/activity/data/setStatusCounts',
            payload: {
              statusCounts: {
                all: Number( counts?.all ) || 0,
                running: Number( counts?.running ) || 0,
                failed: Number( counts?.failed ) || 0,
                completed: Number( counts?.completed ) || 0
              }
            }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        if ( closed ) return;
        console.error( '[activity] Executions status-counts error:', error );
      }
    };

    // Publish the debounced refetch to the factory scope so `onStateChange`
    // (status-chip change) can trigger it too. Refetches the list AND the
    // filter-independent counts — the counts query ignores the chip, so a
    // chip-change re-read is idempotent, and a data-signal keeps both fresh.
    scheduleRefetch = () => {
      if ( debounceTimer ) clearTimeout( debounceTimer );
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        fetchExecutions();
        fetchStatusCounts();
      }, SIGNAL_DEBOUNCE_MS );
    };

    // Initial load — populate the flat list AND the counts before the first
    // signal arrives.
    dispatch(
      {
        type: 'automation/activity/loading/setExecutionsLoading',
        payload: { isLoading: true }
      },
      { source: 'api' }
    );
    fetchStatusCounts();
    fetchExecutions().finally(() => {
      if ( closed ) return;
      dispatch(
        {
          type: 'automation/activity/loading/setExecutionsLoading',
          payload: { isLoading: false }
        },
        { source: 'api' }
      );
    });

    // ONE signal-mode subscription for the whole list — the server sends
    // `{ type: 'signal' }` pings only. The debounced refetch reads the current
    // account-scoped window at fire time.
    const subscription = backend.subscriptions.RuleExecutions(
      {
        _snapshot: false,
        _changes: 'signal'
      },
      ( change: ReadmodelChange<RulesExecutionsReadmodelType> ) => {
        if ( change?.type === 'signal' ) {
          scheduleRefetch?.();
        }
      }
    );

    return () => {
      closed = true;
      scheduleRefetch = null;
      if ( debounceTimer ) {
        clearTimeout( debounceTimer );
        debounceTimer = null;
      }
      if ( subscription && typeof subscription.close === 'function' ) {
        subscription.close();
      }
    };
  }
  };
};
